import React, { useEffect, useMemo, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import T from 'prop-types'
import {
  Checkbox,
  Divider,
  InputAdornment,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import SelectScrollable from './SelectScrollable'

const createOption = (value) => ({ label: value, value })

const createBaseOptions = (options) => {
  if (!options) return []

  return typeof options[0] === 'object' ? options : options.map(createOption)
}

const renderFormatter = (selected, options, maxLength = 17) =>
  selected
    .map((item) => options[item]?.label.substring(0, 6))
    .slice(0, 3)
    .join(',')
    .substring(0, maxLength)

const ITEM_HEIGHT = 54
const ITEM_PADDING_TOP = 8
const SEARCH_FIELD_HEIGHT = 57

export default function FilterSelect({
  options,
  allOption,
  value,
  handleChange,
  readOnly = false,
  multiple = false,
  hideCheckBox,
  hasSearchField = false,
  searchLabel = '',
  fullWidth,
  selectProps,
  sx,
}) {
  const { t } = useTranslation()

  allOption = allOption || {
    value: t('common.form.all'),
    label: t('common.form.all'),
  }

  const [localValue, setLocalValue] = useState(
    multiple ? [allOption.value] : allOption.value
  )

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const baseOptions = useMemo(() => createBaseOptions(options), [options])

  const optionsObject = useMemo(
    () =>
      baseOptions.reduce((acc, next) => {
        acc[next.value] = next
        return acc
      }, {}),
    [baseOptions]
  )

  const [filteredOptions, setFilteredOptions] = useState(baseOptions)

  useEffect(() => {
    setFilteredOptions(baseOptions)
  }, [baseOptions, options])

  const handleChangeLocal = (event, selected) => {
    const {
      target: { value: newValue },
    } = event
    let result
    if (!multiple) {
      result = newValue
    } else {
      result =
        selected.props.value === allOption.value ||
        selected.props.value === undefined ||
        !newValue.length ||
        baseOptions.every((option) => {
          return (
            option.value === allOption.value || newValue.includes(option.value)
          )
        })
          ? [allOption.value]
          : newValue.filter((val) => val !== allOption.value)
    }

    setLocalValue(result)
    handleChange(result)
  }

  const handleChangeSearch = (event) => {
    const {
      target: { value: newValue },
    } = event
    const re = new RegExp(newValue, 'i')
    setFilteredOptions(baseOptions.filter((option) => option.label.match(re)))
  }

  const handleClose = () => {
    setFilteredOptions(baseOptions)
  }

  return (
    <SelectScrollable
      itemHeight={!hideCheckBox ? ITEM_HEIGHT : undefined}
      accessoryHeight={
        ITEM_PADDING_TOP + (hasSearchField ? SEARCH_FIELD_HEIGHT : 0)
      }
      data-testid="filter-select-select-id"
      size="small"
      multiple={multiple}
      value={localValue}
      onChange={handleChangeLocal}
      disabled={readOnly}
      renderValue={(selected) =>
        Array.isArray(selected)
          ? renderFormatter(selected, optionsObject)
          : optionsObject[selected]?.label
      }
      onClose={handleClose}
      MenuProps={{
        disableAutoFocusItem: true,
        MenuListProps: {
          disableListWrap: true,
        },
      }}
      fullWidth={fullWidth !== undefined ? fullWidth : true}
      {...selectProps}
      sx={sx}
    >
      {hasSearchField && (
        <div>
          <TextField
            data-testid="filter-select-text-id"
            placeholder={searchLabel || t('common.form.search')}
            size="small"
            onChange={handleChangeSearch}
            onKeyDown={(e) => {
              // Prevent MUI-Autoselect while typing
              e.stopPropagation()
            }}
            sx={{ width: 1, p: 1, boxSizing: 'border-box' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Divider />
        </div>
      )}
      {filteredOptions.map((option) => {
        return (
          <MenuItem key={option.value} value={option.value}>
            {!hideCheckBox && (
              <Checkbox
                checked={
                  Array.isArray(localValue)
                    ? localValue?.includes(option.value)
                    : localValue === option.value
                }
              />
            )}
            <ListItemText primary={option.label} />
          </MenuItem>
        )
      })}
    </SelectScrollable>
  )
}

FilterSelect.propTypes = {
  options: T.array,
  allOption: T.object,
  value: T.oneOfType([T.array, T.string]).isRequired,
  handleChange: T.func.isRequired,
  readOnly: T.bool,
  multiple: T.bool,
  hideCheckBox: T.bool,
  hasSearchField: T.bool,
  searchLabel: T.string,
  fullWidth: T.bool,
  selectProps: T.object,
  sx: T.object,
}
